/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useMemo, useRef } from 'react';

import { PreviewType, RecordingStatus, UnifiedCollectionType } from 'client-code/previews';
import PropTypes from 'prop-types';
import { RootState, useTivoSelector } from 'src/core/tivo/tivo-redux-wrapper';

import liveIcon from '../../../assets/images/hydra_icon_source_live.png';
import { STAR_RATING_MAX_STARS } from '../../../config';
import { DeviceFeatures, Settings } from '../../../core/device';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { RecordingRestriction } from '../../../core/usecases';
import { Clock, ClockTypes } from '../../Clock';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';
import { RestrictionMessage } from '..';
import { OfferPreviewFormatter } from '../OfferPreviewFormatter';
import { VodPreviewFormatter } from '../VodPreview/VodPreviewFormatter';
import { VodScreenType } from '../VodPreview/VodPreviewProps';
import { TopPreviewProps } from './TopPreviewProps';

import './TopPreview.scss';

const TopPreview: FunctionComponent<TopPreviewProps> = ({
    preview,
    completeOfferPreviewReceived,
    isVod,
}) => {
    const TAG = 'TopPreview';
    const list = useTivoSelector((state: RootState) => state.list);
    const contentPreview = preview?.offerPreview?.contentPreview ?? preview;
    const offerPreviewFormatter = new OfferPreviewFormatter(contentPreview);
    const vodPreviewFormatter = useMemo(
        () => new VodPreviewFormatter(contentPreview, VodScreenType.VOD_SERIES_SCREEN),
        [contentPreview],
    );
    const combinedStatusMessage = useMemo(
        () => vodPreviewFormatter.getCombinedStatusMessage(),
        [vodPreviewFormatter],
    );
    const isVideoWindowEnabled = new Settings().isVideoWindowEnabled();

    const channelLogoUrl = offerPreviewFormatter.getChannelLogoUrl(48, 48);
    const internalRatingsDiv = offerPreviewFormatter
        .getInternalRatings()
        .map((ratingName, index) => (
            <span className="internal-ratings" key={`rating-${ratingName}-${index}`}>
                <span className="rating-text">{ratingName}</span>
            </span>
        ));

    const starRating = offerPreviewFormatter.getStarRating();

    const getRecordingStatusIcon = (): string | undefined => {
        if (contentPreview.recordingStatus === RecordingStatus.InProgressRecording) {
            return 'top-preview-lci-rwl-in-progress-recording';
        }
        if (contentPreview.recordingStatus === RecordingStatus.ScheduledAsOnePass) {
            return 'top-preview-lci-rwl-scheduled-as-one-pass';
        }
        return undefined;
    };

    const renderStars = (type: 'full' | 'half' | 'empty', count: number) => {
        return new Array(count)
            .fill(0)
            .map((_, index) => (
                <span className={`star-icon ${type}`} key={`star-${type}-${index}`}></span>
            ));
    };
    const starRatingDiv = !offerPreviewFormatter.hasStarRating() ? null : (
        <span className="star-rating">
            {renderStars('full', Math.floor(starRating))}
            {starRating > Math.floor(starRating) && renderStars('half', 1)}
            {renderStars('empty', STAR_RATING_MAX_STARS - Math.ceil(starRating))}
        </span>
    );

    const castDiv =
        preview?.cast ||
        (preview.creditString && (
            <div className="cast">
                {offerPreviewFormatter.hasPreviewCastTruncation()
                    ? addGradientEffectToLastTwoLetters(offerPreviewFormatter.getParsedCast())
                    : offerPreviewFormatter.getParsedCast()}
            </div>
        ));

    function addGradientEffectToLastTwoLetters(text: string): JSX.Element {
        const lastTwoChars = text.slice(-2);
        const restOfText = text.slice(0, -2);
        return (
            <>
                {restOfText}
                <span className="gradient-effect">{lastTwoChars}</span>
            </>
        );
    }

    const durationGenreRatingDiv = (
        <div>
            {offerPreviewFormatter.getDurationTime() && (
                <span>{offerPreviewFormatter.getDurationTime()}</span>
            )}

            {offerPreviewFormatter.getDurationTime() && internalRatingsDiv.length > 0 && (
                <span> | </span>
            )}
            {offerPreviewFormatter.getInternalRatings().length > 0 && <>{internalRatingsDiv}</>}
            {offerPreviewFormatter.getInternalRatings().length > 0 &&
                offerPreviewFormatter.getGenres() && <span> | </span>}
            {offerPreviewFormatter.getGenres() && (
                <span className="genre">{offerPreviewFormatter.getGenres()}</span>
            )}
        </div>
    );

    const internalRatingsAndGenreDiv = (
        <div>
            {offerPreviewFormatter.getInternalRatings().length > 0 && <>{internalRatingsDiv}</>}

            {preview?.categoryLabel &&
                offerPreviewFormatter.getGenres() &&
                offerPreviewFormatter.getInternalRatings().length > 0 && <span> | </span>}

            {preview?.categoryLabel && offerPreviewFormatter.getGenres() && (
                <span className="genre">{offerPreviewFormatter.getGenres()}</span>
            )}
        </div>
    );

    const airDateTimeRatingsAndFirstAiredDiv = (
        <div>
            {offerPreviewFormatter.getContentAiringDateTime() && (
                <span>{offerPreviewFormatter.getContentAiringDateTime()}</span>
            )}
            {offerPreviewFormatter.getContentAiringDateTime() &&
                offerPreviewFormatter.getInternalRatings().length > 0 && <span> | </span>}
            {offerPreviewFormatter.getInternalRatings().length > 0 && internalRatingsDiv}
            {(offerPreviewFormatter.getContentAiringDateTime().length > 0 ||
                offerPreviewFormatter.getInternalRatings().length > 0) &&
                offerPreviewFormatter.getFormattedFirstAired() && <span> | </span>}
            {offerPreviewFormatter.getFormattedFirstAired() && (
                <span className="category-first-aired">
                    {offerPreviewFormatter.getFormattedFirstAired()}
                </span>
            )}
        </div>
    );

    const providerImagesDiv = () => {
        const recordingStatusIcon = getRecordingStatusIcon();
        const providerUrls =
            offerPreviewFormatter.getProvidersImageUrls() || preview.providersImageUrls;
        const isLive = preview?.isLive || false;
        return (
            <div className="provider-images-record-status">
                {list?.['collection-filters']?.isFocused && isLive && (
                    <ImageLoader
                        src={liveIcon}
                        imageType={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                    />
                )}
                {recordingStatusIcon && <span className={`${recordingStatusIcon}`} />}
                {channelLogoUrl && (
                    <ImageLoader
                        src={channelLogoUrl}
                        imageType={ImageLoaderTypes.CHANNEL_LOGO_PREVIEW_ICON}
                    />
                )}
                {DeviceFeatures.isSocuEnabled() &&
                    providerUrls &&
                    providerUrls.length > 0 &&
                    providerUrls.map((url: string, index: number) => (
                        <ImageLoader
                            key={index}
                            src={url}
                            imageType={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                        />
                    ))}
            </div>
        );
    };

    const ratingsAndEntitlementDiv = useMemo(
        () => (
            <div>
                {internalRatingsDiv}
                {internalRatingsDiv.length > 0 && combinedStatusMessage && <span> | </span>}
                {combinedStatusMessage && (
                    <span className="entitlement">{combinedStatusMessage}</span>
                )}
            </div>
        ),
        [internalRatingsDiv, combinedStatusMessage],
    );

    const itemRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const hasTitleOverflowed =
        (titleRef.current?.scrollWidth || 0) > (itemRef.current?.clientWidth || 0);

    const renderPreviewContent = () => {
        const getPreviewType = () => {
            return (
                preview?.offerPreview?.contentPreview?.previewType ||
                preview?.previewType ||
                preview?.unifiedCollectionType
            );
        };
        const previewType = getPreviewType();
        const shouldApplyTruncation = () => {
            return (
                previewType === PreviewType.ProgramPreview ||
                previewType === PreviewType.MoviePreview ||
                previewType === UnifiedCollectionType.SeriesPreviewV2
            );
        };

        const getSafeDescription = () => {
            const description = offerPreviewFormatter.getPreviewDescription();
            return description?.trim()
                ? description
                : Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE);
        };

        const descriptionContent =
            shouldApplyTruncation() && offerPreviewFormatter.hasPreviewCastDivTruncation() ? (
                <>
                    {addGradientEffectToLastTwoLetters(
                        offerPreviewFormatter.getPreviewDescriptionTruncated(),
                    )}
                    <span className="info-btn" />
                </>
            ) : (
                getSafeDescription()
            );

        const ccVideoResolutionText = offerPreviewFormatter.getCcVideoResolution();
        const titleAndDescriptionDiv = (
            <>
                <div ref={itemRef} className="top-row">
                    <div
                        ref={titleRef}
                        className={`top-${hasTitleOverflowed ? 'overflow-' : ''}${offerPreviewFormatter.getPreviewTitleClasses()}`}
                    >
                        {offerPreviewFormatter.getPreviewTitle()?.trim()
                            ? offerPreviewFormatter.getPreviewTitle()
                            : Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE)}
                    </div>

                    <Clock type={ClockTypes.COLLECTION_SCREEN_CLOCK} />
                </div>
                <div className={`top-${offerPreviewFormatter.getPreviewDescriptionClasses()}`}>
                    {descriptionContent}
                    {ccVideoResolutionText.length > 0 ? (
                        <span className="cc_stream">{ccVideoResolutionText}</span>
                    ) : null}
                </div>
            </>
        );
        Log.d(TAG, 'previewType: ', previewType);
        switch (previewType) {
            case PreviewType.ProgramPreview:
                return (
                    <>
                        {titleAndDescriptionDiv}
                        {castDiv}
                        {durationGenreRatingDiv}
                        {providerImagesDiv()}
                    </>
                );
            case PreviewType.EpisodePreview:
                return (
                    <>
                        {titleAndDescriptionDiv}
                        {isVod ? ratingsAndEntitlementDiv : airDateTimeRatingsAndFirstAiredDiv}
                        {providerImagesDiv()}
                    </>
                );
            case PreviewType.MoviePreview:
                return (
                    <>
                        {titleAndDescriptionDiv}
                        {castDiv}
                        {durationGenreRatingDiv}
                        {starRatingDiv}
                        {providerImagesDiv()}
                    </>
                );
            case UnifiedCollectionType.SeriesPreviewV2:
                return (
                    <>
                        {titleAndDescriptionDiv}
                        {castDiv}
                        {internalRatingsAndGenreDiv}
                        {providerImagesDiv()}
                    </>
                );
            case UnifiedCollectionType.LeaguePreview:
            case UnifiedCollectionType.TeamPreview:
                return titleAndDescriptionDiv;
            default:
                return completeOfferPreviewReceived ? titleAndDescriptionDiv : null;
        }
    };

    return (
        <div
            className={`top-preview-container ${isVideoWindowEnabled ? 'top-preview-container-with-pig' : ''}`}
        >
            {renderPreviewContent()}
            <RestrictionMessage
                displayType={RecordingRestriction.PREVIEW_AREA}
                preview={preview?.offerPreview ?? preview}
            />
        </div>
    );
};

TopPreview.propTypes = {
    preview: PropTypes.any,
};

export default TopPreview;
